import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  TableContainer,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  StatCard,
  StatNumber,
  StatLabel,
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
  SkeletonTableRow,
  SkeletonTableCell,
  SkeletonText,
  SkeletonTextShort,
  Button,
  Input,
  Select,
  Pagination,
  PaginationButton,
  PaginationInfo,
  Container
} from '../ui';

interface Company {
  _id: string;
  name: string;
  headquarters?: string;
  website?: string;
  industry?: string;
  created_at: string;
  updated_at: string;
}

interface CompanyStats {
  totalCompanies: number;
  recentCompaniesCount: number;
  companiesByIndustry: { [key: string]: number };
  recentCompanies: Company[];
}

const INDUSTRIES = [
  'Automotive',
  'Bancario-Assicurativo',
  'Beauty',
  'Carburanti',
  'Consulenza',
  'Elettronica',
  'Energia',
  'Finanziario',
  'Fitness',
  'Food & Beverage',
  'Immobiliare',
  'Industria',
  'Moda',
  'Pet Care',
  'Pharma',
  'Retail',
  'Tech',
  'Telefonia'
];

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const CompanyWebsite = styled.a`
  color: #667eea;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ClickableCell = styled(TableCell)`
  cursor: pointer;
  font-weight: 600;
  color: #1f2937;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const CompaniesManagement: React.FC = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to get initial state from URL
  const getInitialParam = (key: string, defaultVal: string = '') => {
    const params = new URLSearchParams(location.search);
    return params.get(key) || defaultVal;
  };

  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => getInitialParam('search'));
  const [industryFilter, setIndustryFilter] = useState(() => getInitialParam('industry'));
  const [currentPage, setCurrentPage] = useState(() => {
    const page = parseInt(getInitialParam('page', '1'), 10);
    return isNaN(page) || page < 1 ? 1 : page;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const pendingScrollY = useRef<number | null>(null);

  const pageSize = 10;
  const expectedRowCount = totalCompanies > 0
    ? Math.min(pageSize, Math.max(totalCompanies - (currentPage - 1) * pageSize, 0))
    : 0;
  const skeletonRowCount = expectedRowCount > 0
    ? expectedRowCount
    : (companies.length > 0 ? companies.length : pageSize);
  const canManageCompanies = hasRole(['admin', 'super_user']);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (industryFilter) params.set('industry', industryFilter);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    navigate(`?${params.toString()}`, { replace: true });
  }, [searchTerm, industryFilter, currentPage, navigate]);

  // Restore scroll position from sessionStorage (when returning from detail page)
  useEffect(() => {
    const saved = sessionStorage.getItem('companiesScrollY');
    if (saved) {
      pendingScrollY.current = parseInt(saved, 10);
      sessionStorage.removeItem('companiesScrollY');
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    if (canManageCompanies) {
      fetchStats();
    }
  }, [currentPage, searchTerm, industryFilter]);

  useEffect(() => {
    if (!loading && pendingScrollY.current !== null) {
      window.scrollTo({ top: pendingScrollY.current, behavior: 'auto' });
      pendingScrollY.current = null;
    }
  }, [loading]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(industryFilter && { industry: industryFilter })
      });

      const response = await axios.get(`/api/companies?${params}`);
      if (response.data.success) {
        setCompanies(response.data.data.companies);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalCompanies(response.data.data.pagination.total);
        
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/companies/stats/overview');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch company stats:', error);
    }
  };

  const navigateWithScroll = (path: string) => {
    sessionStorage.setItem('companiesScrollY', String(window.scrollY));
    navigate(path);
  };

  const handleCreateCompany = () => {
    navigateWithScroll('/dashboard/companies/new');
  };

  const handlePageChange = (nextPage: number) => {
    pendingScrollY.current = window.scrollY;
    setCurrentPage(nextPage);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatWebsiteUrl = (website: string) => {
    if (!website) return '';
    return website.startsWith('http') ? website : `https://${website}`;
  };

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h2 style={{ color: '#1f2937', fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
          Gestione Aziende
        </h2>
        {canManageCompanies && (
          <Button variant="primary" onClick={handleCreateCompany}>
            ➕ Aggiungi Azienda
          </Button>
        )}
      </div>

      {canManageCompanies && stats && (
        <StatsContainer>
          <StatCard>
            <StatNumber>{stats.totalCompanies || 0}</StatNumber>
            <StatLabel>Totale Aziende</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.companiesByIndustry ? Object.keys(stats.companiesByIndustry).length : 0}</StatNumber>
            <StatLabel>Settori</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.recentCompaniesCount || 0}</StatNumber>
            <StatLabel>Aggiunte Recenti</StatLabel>
          </StatCard>
        </StatsContainer>
      )}

      <FiltersContainer>
        <Input
          type="text"
          placeholder="Cerca aziende per nome..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{ flex: 1, minWidth: '300px' }}
        />
        <Select
          value={industryFilter}
          onChange={(e) => {
            setIndustryFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ minWidth: '200px' }}
          size="medium"
        >
          <option value="">Tutti i Settori</option>
          {INDUSTRIES.map(industry => (
            <option key={industry} value={industry}>
              {industry}
            </option>
          ))}
        </Select>
      </FiltersContainer>

      <TableContainer>
        <TableHeader>
          <TableCell>Nome Azienda</TableCell>
          <TableCell>Sede</TableCell>
          <TableCell>Sito Web</TableCell>
          <TableCell>Settore</TableCell>
          <TableCell>Creata</TableCell>
        </TableHeader>
        <TableBody style={{ '--skeleton-line-height': '22.4px' } as React.CSSProperties}>
          {loading ? (
            Array.from({ length: skeletonRowCount }).map((_, index) => (
              <SkeletonTableRow key={index}>
                <SkeletonTableCell><SkeletonText /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
              </SkeletonTableRow>
            ))
          ) : (
            companies.map(company => (
              <TableRow
                key={company._id}
                onClick={() => navigateWithScroll(`/dashboard/companies/${company._id}`)}
                style={{ cursor: 'pointer' }}
              >
                <ClickableCell title={company.name}>
                  {company.name}
                </ClickableCell>
                <TableCell title={company.headquarters || '-'}>
                  {company.headquarters || '-'}
                </TableCell>
                <TableCell title={company.website || '-'}>
                  {company.website ? (
                    <CompanyWebsite 
                      href={formatWebsiteUrl(company.website)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {company.website}
                    </CompanyWebsite>
                  ) : '-'}
                </TableCell>
                <TableCell title={company.industry || '-'}>
                  {company.industry || '-'}
                </TableCell>
                <TableCell title={formatDate(company.created_at)}>
                  {formatDate(company.created_at)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </TableContainer>

      {!loading && companies.length === 0 && (
        <EmptyState>
          <EmptyStateTitle>Nessuna azienda trovata</EmptyStateTitle>
          <EmptyStateText>
            {searchTerm || industryFilter
              ? 'Nessuna azienda trovata con i filtri selezionati.'
              : 'Nessuna azienda ancora. Crea la tua prima azienda per iniziare.'}
          </EmptyStateText>
        </EmptyState>
      )}

      <Pagination>
        <PaginationButton
          disabled={loading || currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Precedente
        </PaginationButton>
        <PaginationInfo>
          Pagina {currentPage} di {totalPages} ({totalCompanies} aziende totali)
        </PaginationInfo>
        <PaginationButton
          disabled={loading || currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Successivo
        </PaginationButton>
      </Pagination>
    </Container>
  );
};

export default CompaniesManagement;
